/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CButton, CSpinner, CAlert, CFormSelect,
  CModal, CModalHeader, CModalTitle, CModalBody, CListGroup, CListGroupItem
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';
import { getOrderDropdown } from '../../services/endpoints';
import { formatDateForInput, formatDateForBackend } from '../../helpers/dateHelper';
import './OrderInputs.scss';
import SearchableSelect from '../SearchableSelect/SearchableSelect';

const OrderInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    OrderNo: '',
    PrdProductGuid: '',
    OpsCustomerGuid: '',
    PrdUnitGuid: '',
    PaymentTerm: '',
    ShipmentPlace: '',
    OpsOrderStatusGuid: '',
    OrderDatetime: '',
    OrderDeadlineDatetime: '',
    ProductQuantity: '',
    ProductionStartDatetime: '',
    ProductionEndDatetime: '',
    ProductDispatchQuantity: '',
    ProductStockQuantity: '',
    ProductRemainingQuantity: '',
    ProductCostPrice: '',
    ProductSalesPrice: '',
    ProductTotalPrice: '',
    OrderDescription: '',
    CostCurrencyGuid: '',
    SalesCurrencyGuid: '',
    CustomerCode: '',
    Note: ''
  });

  // Separate state for price inputs
  const [priceInputs, setPriceInputs] = useState({
    costPriceWhole: '',
    costPriceDecimal: '',
    salesPriceWhole: '',
    salesPriceDecimal: '',
    totalPriceWhole: '',
    totalPriceDecimal: ''
  });

  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [productOptions, setProductOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [costSearchTerm, setCostSearchTerm] = useState('');
  const [salesSearchTerm, setSalesSearchTerm] = useState('');
  const [filteredCostCurrencies, setFilteredCostCurrencies] = useState([]);
  const [filteredSalesCurrencies, setFilteredSalesCurrencies] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [dropdownError, setDropdownError] = useState(null);

  const [showCostCurrencyModal, setShowCostCurrencyModal] = useState(false);
  const [showSalesCurrencyModal, setShowSalesCurrencyModal] = useState(false);

  const fetchProductOptionsBySearch = async (searchTerm) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    const payload = {
      SessionUserId: userId,
      OrderId: '',
      IncludeOrders: false,
      IncludeProducts: true,
      IncludeWeaveColors: false,
      ProductSearchTerm: searchTerm || '',
    };

    const response = await getOrderDropdown(payload);
    return response?.data?.Products || [];
  };

  useEffect(() => {
    if (initialData) {
      // Parse cost price
      if (initialData.ProductCostPrice) {
        const costPrice = (initialData.ProductCostPrice / 100).toFixed(2);
        const [costWhole, costDecimal] = costPrice.split('.');
        setPriceInputs(prev => ({
          ...prev,
          costPriceWhole: costWhole,
          costPriceDecimal: costDecimal || '00'
        }));
      }

      // Parse sales price
      if (initialData.ProductSalesPrice) {
        const salesPrice = (initialData.ProductSalesPrice / 100).toFixed(2);
        const [salesWhole, salesDecimal] = salesPrice.split('.');
        setPriceInputs(prev => ({
          ...prev,
          salesPriceWhole: salesWhole,
          salesPriceDecimal: salesDecimal || '00'
        }));
      }

      // Parse total price
      if (initialData.ProductTotalPrice) {
        const totalPrice = (initialData.ProductTotalPrice / 100).toFixed(2);
        const [totalWhole, totalDecimal] = totalPrice.split('.');
        setPriceInputs(prev => ({
          ...prev,
          totalPriceWhole: totalWhole,
          totalPriceDecimal: totalDecimal || '00'
        }));
      }

      setFormData({
        OrderNo: initialData.OrderNo || '',
        PrdProductGuid: initialData.PrdProductGuid || '',
        OpsCustomerGuid: initialData.OpsCustomerGuid || '',
        PrdUnitGuid: initialData.PrdUnitGuid || '',
        PaymentTerm: initialData.PaymentTerm || '',
        ShipmentPlace: initialData.ShipmentPlace || '',
        OpsOrderStatusGuid: initialData.OpsOrderStatusGuid || '',
        OrderDatetime: initialData.OrderDatetime || '',
        OrderDeadlineDatetime: initialData.OrderDeadlineDatetime || '',
        ProductQuantity: initialData.ProductQuantity || '',
        ProductionStartDatetime: initialData.ProductionStartDatetime || '',
        ProductionEndDatetime: initialData.ProductionEndDatetime || '',
        ProductDispatchQuantity: initialData.ProductDispatchQuantity || '',
        ProductStockQuantity: initialData.ProductStockQuantity || '',
        ProductRemainingQuantity: initialData.ProductRemainingQuantity || '',
        ProductCostPrice: initialData.ProductCostPrice ? (initialData.ProductCostPrice / 100).toFixed(2) : '',
        ProductSalesPrice: initialData.ProductSalesPrice ? (initialData.ProductSalesPrice / 100).toFixed(2) : '',
        ProductTotalPrice: initialData.ProductTotalPrice ? (initialData.ProductTotalPrice / 100).toFixed(2) : '',
        OrderDescription: initialData.Description || '',
        CostCurrencyGuid: initialData.CostCurrencyGuid || '',
        SalesCurrencyGuid: initialData.SalesCurrencyGuid || '',
        CustomerCode: initialData.CustomerCode || '',
        Note: initialData.Note || ''
      });
    }
  }, [initialData]);

  // Update formData when price inputs change
  useEffect(() => {
    const costPrice = priceInputs.costPriceWhole || priceInputs.costPriceDecimal
      ? `${priceInputs.costPriceWhole || '0'}.${priceInputs.costPriceDecimal || '00'}`
      : '';

    const salesPrice = priceInputs.salesPriceWhole || priceInputs.salesPriceDecimal
      ? `${priceInputs.salesPriceWhole || '0'}.${priceInputs.salesPriceDecimal || '00'}`
      : '';

    setFormData(prev => ({
      ...prev,
      ProductCostPrice: costPrice,
      ProductSalesPrice: salesPrice
    }));
  }, [priceInputs.costPriceWhole, priceInputs.costPriceDecimal, priceInputs.salesPriceWhole, priceInputs.salesPriceDecimal]);

  // Calculate total price whenever quantity or sales price changes
  useEffect(() => {
    const quantity = parseFloat(formData.ProductQuantity);
    const salesWhole = parseInt(priceInputs.salesPriceWhole || '0', 10);
    const salesDecimal = parseInt((priceInputs.salesPriceDecimal || '00').padEnd(2, '0').slice(0, 2), 10);
    const salesCents = salesWhole * 100 + (Number.isNaN(salesDecimal) ? 0 : salesDecimal);

    if (quantity > 0 && salesCents > 0) {
      const totalCents = Math.round(quantity * salesCents);
      const totalWhole = Math.floor(totalCents / 100).toString();
      const totalDecimal = (totalCents % 100).toString().padStart(2, '0');

      setPriceInputs(prev => ({
        ...prev,
        totalPriceWhole: totalWhole,
        totalPriceDecimal: totalDecimal
      }));

      setFormData(prev => ({ ...prev, ProductTotalPrice: `${totalWhole}.${totalDecimal}` }));
    } else if (!formData.ProductQuantity || (!priceInputs.salesPriceWhole && !priceInputs.salesPriceDecimal)) {
      setPriceInputs(prev => ({
        ...prev,
        totalPriceWhole: '',
        totalPriceDecimal: ''
      }));
      setFormData(prev => ({ ...prev, ProductTotalPrice: '' }));
    }
  }, [formData.ProductQuantity, formData.ProductSalesPrice]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData(prev => ({
            ...prev,
            ProductQuantity: '',
            ProductCostPrice: '',
            ProductSalesPrice: '',
            ProductTotalPrice: '',
            PaymentTerm: '',
            ShipmentPlace: '',
            OrderDescription: '',
            Note: ''
          }));
          setPriceInputs({
            costPriceWhole: '',
            costPriceDecimal: '',
            salesPriceWhole: '',
            salesPriceDecimal: '',
            totalPriceWhole: '',
            totalPriceDecimal: ''
          });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      setDropdownLoading(true);
      setDropdownError(null);
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;
        const payload = {
          SessionUserId: userId,
          OrderId: '',
          IncludeOrders: false,
          IncludeProducts: false,
          IncludeWeaveColors: false,
        };
        const response = await getOrderDropdown(payload);
        const data = response?.data || {};
        setProductOptions([]);
        setCustomerOptions(data.Customers || []);

        // Normalize currencies from new dropdown shape
        const currenciesRaw = data.Currencies || [];
        const normalizedCurrencies = currenciesRaw.map(c => ({
          PrdCurrencyGuid: c.PrdCurrencyGuid || c.CostCurrencyGuid || c.SalesCurrencyGuid,
          CurrencyName: c.CurrencyName || c.CostCurrencyName || c.SalesCurrencyName,
          CurrencyCode: c.CurrencyCode || c.CostCurrencyCode || c.SalesCurrencyCode,
        })).filter(c => c.PrdCurrencyGuid);
        setCurrencyOptions(normalizedCurrencies);
        setFilteredCostCurrencies(normalizedCurrencies);
        setFilteredSalesCurrencies(normalizedCurrencies);
        setOrderStatusOptions(data.OrderStatuses || []);
        setUnitOptions(data.Units || []);

        // Set default TRY currency if not selected and available
        const getTryCurrencyGuid = (list) => {
          if (!Array.isArray(list) || list.length === 0) return '';
          const byCode = list.find(c => (c.CurrencyCode || '').toLowerCase() === 'try');
          if (byCode) return byCode.PrdCurrencyGuid;
          const byName = list.find(c => {
            const name = (c.CurrencyName || '').toLowerCase();
            return name.includes('türk') || name.includes('turk') || name.includes('lira') || name.includes('tl');
          });
          return byName ? byName.PrdCurrencyGuid : '';
        };

        const tryGuid = getTryCurrencyGuid(normalizedCurrencies);
        if (tryGuid) {
          setFormData(prev => ({
            ...prev,
            CostCurrencyGuid: prev.CostCurrencyGuid || tryGuid,
            SalesCurrencyGuid: prev.SalesCurrencyGuid || tryGuid,
          }));
        }
      } catch (err) {
        setDropdownError('Dropdown verileri yüklenemedi.');
        setProductOptions([]);
        setCustomerOptions([]);
        setCurrencyOptions([]);
        setOrderStatusOptions([]);
        setUnitOptions([]);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const loadSelectedProductOption = async () => {
      if (!formData.PrdProductGuid) return;

      try {
        const products = await fetchProductOptionsBySearch(formData.PrdProductGuid);
        if (Array.isArray(products) && products.length > 0) {
          setProductOptions(products);
        }
      } catch (error) {
        // no-op
      }
    };

    loadSelectedProductOption();
  }, [formData.PrdProductGuid]);

  const handleProductSearch = async (searchTerm) => {
    const mergeSelectedProductOption = (incomingProducts, previousProducts, selectedGuid) => {
      const nextProducts = Array.isArray(incomingProducts) ? incomingProducts : [];
      if (!selectedGuid) return nextProducts;

      const hasSelectedInNext = nextProducts.some(
        (item) => String(item?.PrdProductGuid || '') === String(selectedGuid)
      );

      if (hasSelectedInNext) return nextProducts;

      const selectedFromPrevious = (Array.isArray(previousProducts) ? previousProducts : []).find(
        (item) => String(item?.PrdProductGuid || '') === String(selectedGuid)
      );

      return selectedFromPrevious ? [selectedFromPrevious, ...nextProducts] : nextProducts;
    };

    try {
      const products = await fetchProductOptionsBySearch(searchTerm);
      setProductOptions((prev) => mergeSelectedProductOption(products, prev, formData.PrdProductGuid));
    } catch (error) {
      setProductOptions((prev) => mergeSelectedProductOption([], prev, formData.PrdProductGuid));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePriceInputChange = (type, part, value) => {
    // For whole part, allow only numbers
    if (part === 'whole') {
      if (!/^\d*$/.test(value)) return;
    }

    // For decimal part, allow only 2 digits max
    if (part === 'decimal') {
      if (!/^\d{0,2}$/.test(value)) return;
      // Don't auto-pad here, let user type freely
    }

    setPriceInputs(prev => ({
      ...prev,
      [`${type}Price${part.charAt(0).toUpperCase() + part.slice(1)}`]: value
    }));

    // Clear errors for the price field
    if (type === 'cost' && errors.ProductCostPrice) {
      setErrors(prev => ({ ...prev, ProductCostPrice: '' }));
    }
    if (type === 'sales' && errors.ProductSalesPrice) {
      setErrors(prev => ({ ...prev, ProductSalesPrice: '' }));
    }
  };

  const handleDecimalKeyDown = (e, type, part) => {
    // If user presses dot or comma in whole part, focus decimal input
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById(`${type}PriceDecimal`);
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try {
            decimalInput.setSelectionRange(valueLength, valueLength);
          } catch (err) {
            // Some browsers/inputs may not support setSelectionRange; ignore
          }
        }
        return;
      }
    }

    // Allow: backspace, delete, tab, escape, enter, arrows
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }

    // For decimal part, check if we already have 2 digits
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;

      // If we have 2 digits and no selection, prevent input
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }

    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleDecimalFocus = (type, part) => {
    // Don't auto-select for decimal part to allow easier editing
    if (part === 'whole') {
      const input = document.getElementById(`${type}PriceWhole`);
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleDecimalBlur = (type, part) => {
    // Only pad with zeros on blur if the field is not empty
    if (part === 'decimal') {
      const currentValue = priceInputs[`${type}PriceDecimal`];
      // If user entered something, pad it
      if (currentValue !== '' && currentValue.length === 1) {
        setPriceInputs(prev => ({
          ...prev,
          [`${type}PriceDecimal`]: currentValue.padEnd(2, '0')
        }));
      }
      // If completely empty and whole part has value, set to 00
      else if (currentValue === '' && priceInputs[`${type}PriceWhole`]) {
        setPriceInputs(prev => ({
          ...prev,
          [`${type}PriceDecimal`]: '00'
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.PrdProductGuid?.toString().trim()) newErrors.PrdProductGuid = t('product') + ' ' + t('required');
    if (!formData.ProductQuantity?.toString().trim()) newErrors.ProductQuantity = t('productQuantity') + ' ' + t('required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }
      // Safely convert price inputs to integer cents (avoid float precision)
      const toCentsFromParts = (wholeStr, decimalStr) => {
        const whole = parseInt(wholeStr || '0', 10);
        const dec = parseInt((decimalStr || '00').padEnd(2, '0').slice(0, 2), 10);
        if (Number.isNaN(whole) && Number.isNaN(dec)) return '';
        return String((Number.isNaN(whole) ? 0 : whole) * 100 + (Number.isNaN(dec) ? 0 : dec));
      };

      const costCents = toCentsFromParts(priceInputs.costPriceWhole, priceInputs.costPriceDecimal);
      const salesCents = toCentsFromParts(priceInputs.salesPriceWhole, priceInputs.salesPriceDecimal);

      // Total in cents using quantity * salesCents (rounded)
      const qty = parseFloat(formData.ProductQuantity || '0');
      const totalCents = qty > 0 && salesCents !== '' ? String(Math.round(qty * parseInt(salesCents, 10))) : '';

      // Resolve currency fallbacks to TRY if empty
      const getTryCurrencyGuid = (list) => {
        if (!Array.isArray(list) || list.length === 0) return '';
        const byCode = list.find(c => (c.CurrencyCode || '').toLowerCase() === 'try');
        if (byCode) return byCode.PrdCurrencyGuid;
        const byName = list.find(c => {
          const name = (c.CurrencyName || '').toLowerCase();
          return name.includes('türk') || name.includes('turk') || name.includes('lira') || name.includes('tl');
        });
        return byName ? byName.PrdCurrencyGuid : '';
      };

      const tryGuid = getTryCurrencyGuid(currencyOptions);

      const submitData = {
        SessionUserId: String(userId),
        OrderNo: formData.OrderNo?.toString().trim() || '',
        PrdProductGuid: formData.PrdProductGuid?.toString().trim() || '',
        OpsCustomerGuid: formData.OpsCustomerGuid?.toString().trim() || '',
        PrdUnitGuid: formData.PrdUnitGuid?.toString().trim() || '',
        PaymentTerm: formData.PaymentTerm?.toString().trim() || '',
        ShipmentPlace: formData.ShipmentPlace?.toString().trim() || '',
        OpsOrderStatusGuid: formData.OpsOrderStatusGuid?.toString().trim() || '',
        OrderDatetime: formatDateForBackend(formData.OrderDatetime) || '',
        OrderDeadlineDatetime: formatDateForBackend(formData.OrderDeadlineDatetime) || '',
        ProductQuantity: formData.ProductQuantity?.toString().trim() || '',
        ProductionStartDatetime: formatDateForBackend(formData.ProductionStartDatetime) || '',
        ProductionEndDatetime: formatDateForBackend(formData.ProductionEndDatetime) || '',
        ProductDispatchQuantity: formData.ProductDispatchQuantity?.toString().trim() || '',
        ProductStockQuantity: formData.ProductStockQuantity?.toString().trim() || '',
        ProductRemainingQuantity: formData.ProductRemainingQuantity?.toString().trim() || '',
        ProductCostPrice: costCents,
        ProductSalesPrice: salesCents,
        ProductTotalPrice: totalCents,
        Description: formData.OrderDescription?.toString().trim() || '',
        CostCurrencyGuid: formData.CostCurrencyGuid?.toString().trim() || tryGuid || '',
        SalesCurrencyGuid: formData.SalesCurrencyGuid?.toString().trim() || tryGuid || '',
        CustomerCode: formData.CustomerCode?.toString().trim() || '',
        Note: formData.Note?.toString().trim() || ''
      };
      if (isEdit && initialData?.OrderGuid) {
        submitData.OrderId = initialData.OrderGuid;
      }
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData(prev => ({
        ...prev,
        ProductQuantity: '',
        ProductCostPrice: '',
        ProductSalesPrice: '',
        ProductTotalPrice: '',
        PaymentTerm: '',
        ShipmentPlace: '',
        OrderDescription: '',
        Note: ''
      }));
      setPriceInputs({
        costPriceWhole: '',
        costPriceDecimal: '',
        salesPriceWhole: '',
        salesPriceDecimal: '',
        totalPriceWhole: '',
        totalPriceDecimal: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateOrder') : t('createOrder');
  };

  // Get currency symbol by guid
  const getCurrencySymbolByGuid = (guid) => {
    if (!guid) return '';
    const selectedCurrency = currencyOptions.find(c => c.PrdCurrencyGuid === guid);
    if (!selectedCurrency) return '';
    const currencyName = selectedCurrency.CurrencyName?.toLowerCase() || '';
    const currencyCode = selectedCurrency.CurrencyCode?.toLowerCase() || '';
    if (currencyCode === 'eur' || currencyCode === 'euro') return '€';
    if (currencyCode === 'usd' || currencyCode === 'dollar') return '$';
    if (currencyCode === 'try' || currencyCode === 'tl' || currencyCode === 'turkish lira') return '₺';
    if (currencyCode === 'gbp' || currencyCode === 'pound') return '£';
    if (currencyCode === 'jpy' || currencyCode === 'yen') return '¥';
    if (currencyCode === 'cny' || currencyCode === 'yuan') return '¥';
    if (currencyCode === 'inr' || currencyCode === 'rupee') return '₹';
    if (currencyCode === 'rub' || currencyCode === 'ruble') return '₽';
    if (currencyName.includes('euro') || currencyName.includes('eur')) return '€';
    if (currencyName.includes('dolar') || currencyName.includes('dollar') || currencyName.includes('usd')) return '$';
    if (currencyName.includes('türk') || currencyName.includes('turk') || currencyName.includes('try') || currencyName.includes('tl') || currencyName.includes('lira')) return '₺';
    if (currencyName.includes('pound') || currencyName.includes('sterlin')) return '£';
    if (currencyName.includes('yen')) return '¥';
    if (currencyName.includes('yuan')) return '¥';
    if (currencyName.includes('rupee')) return '₹';
    if (currencyName.includes('ruble')) return '₽';
    return selectedCurrency.CurrencyName || '';
  };

  // Get unit symbol based on selected unit
  const getUnitSymbol = () => {
    if (!formData.PrdUnitGuid) return '';

    const selectedUnit = unitOptions.find(unit =>
      unit.PrdUnitGuid === formData.PrdUnitGuid
    );

    if (!selectedUnit) return '';

    return selectedUnit.UnitName || '';
  };

  return (
    <div className="order-inputs-container">
      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}
      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">{submitResponse.Result.ErrorDescription}</CAlert>
      )}
      {dropdownError && (
        <CAlert color="danger">{dropdownError}</CAlert>
      )}
      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="OrderNo">{t('orderNo')}</CFormLabel>
            <CFormInput
              type="text"
              id="OrderNo"
              name="OrderNo"
              value={formData.OrderNo}
              readOnly
              disabled
              className="order-no-field"
              placeholder={t('orderNo')}
            />
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="PrdProductGuid">{t('product')}</CFormLabel>
            <SearchableSelect
              id="PrdProductGuid"
              name="PrdProductGuid"
              options={productOptions.map(item => ({
                ...item,
                Name: [item.WeaveCode, item.YarnName, item.YarnCode].filter(Boolean).join(' - ') || item.WeaveCode || item.YarnName || item.YarnCode || '-'
              }))}
              value={formData.PrdProductGuid}
              onChange={(selectedValue) => handleInputChange({ target: { name: 'PrdProductGuid', value: selectedValue } })}
              onSearchTermChange={handleProductSearch}
              placeholder={dropdownLoading ? t('loading') : t('select')}
              disabled={isLoading || dropdownLoading}
              invalid={!!errors.PrdProductGuid}
              displayKey="Name"
              valueKey="PrdProductGuid"
              emptyText={t('No options found')}
            />
            {errors.PrdProductGuid && <div className="invalid-feedback d-block">{errors.PrdProductGuid}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="OpsOrderStatusGuid">{t('orderStatus')}</CFormLabel>
            <CFormSelect
              id="OpsOrderStatusGuid"
              name="OpsOrderStatusGuid"
              className={errors.OpsOrderStatusGuid ? 'is-invalid' : ''}
              value={formData.OpsOrderStatusGuid}
              onChange={handleInputChange}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {orderStatusOptions.map((item) => (
                <option key={item.OpsOrderStatusGuid} value={item.OpsOrderStatusGuid}>
                  {item.Name || item.OpsOrderStatusGuid}
                </option>
              ))}
            </CFormSelect>
            {errors.OpsOrderStatusGuid && <div className="invalid-feedback d-block">{errors.OpsOrderStatusGuid}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="OpsCustomerGuid">{t('customer')}</CFormLabel>
            <SearchableSelect
              id="OpsCustomerGuid"
              name="OpsCustomerGuid"
              options={customerOptions.map(item => ({
                ...item,
                Name: item.CustomerName || item.OpsCustomerGuid
              }))}
              value={formData.OpsCustomerGuid}
              onChange={(selectedValue) => handleInputChange({ target: { name: 'OpsCustomerGuid', value: selectedValue } })}
              placeholder={dropdownLoading ? t('loading') : t('select')}
              disabled={isLoading || dropdownLoading}
              invalid={!!errors.OpsCustomerGuid}
              displayKey="Name"
              valueKey="OpsCustomerGuid"
              emptyText={t('No options found')}
            />
            {errors.OpsCustomerGuid && <div className="invalid-feedback d-block">{errors.OpsCustomerGuid}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="CustomerCode">{t('customerProductCode')}</CFormLabel>
            <CFormInput
              type="text"
              id="CustomerCode"
              name="CustomerCode"
              value={formData.CustomerCode}
              onChange={handleInputChange}
              placeholder={t('customerCode')}
              disabled={isLoading}
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="ProductQuantity">
              {t('productQuantity')}
              {getUnitSymbol() && <span className="currency-unit-info">({getUnitSymbol()})</span>}
            </CFormLabel>
            <CFormInput
              type="number"
              min="0"
              id="ProductQuantity"
              name="ProductQuantity"
              value={formData.ProductQuantity}
              onChange={handleInputChange}
              invalid={!!errors.ProductQuantity}
              placeholder={getUnitSymbol() ? `0 ${getUnitSymbol()}` : t('productQuantity')}
              disabled={isLoading}
            />
            {errors.ProductQuantity && <div className="invalid-feedback d-block">{errors.ProductQuantity}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="PrdUnitGuid">{t('unit')}</CFormLabel>
            <CFormSelect
              id="PrdUnitGuid"
              name="PrdUnitGuid"
              className={errors.PrdUnitGuid ? 'is-invalid' : ''}
              value={formData.PrdUnitGuid}
              onChange={handleInputChange}
              disabled={isLoading || dropdownLoading}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {unitOptions.map((item) => (
                <option key={item.PrdUnitGuid} value={item.PrdUnitGuid}>
                  {item.UnitName || item.PrdUnitGuid}
                </option>
              ))}
            </CFormSelect>
            {errors.PrdUnitGuid && <div className="invalid-feedback d-block">{errors.PrdUnitGuid}</div>}
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="PaymentTerm">{t('paymentTerm') || 'Payment Term'}</CFormLabel>
            <CFormInput
              type="text"
              id="PaymentTerm"
              name="PaymentTerm"
              value={formData.PaymentTerm}
              onChange={handleInputChange}
              placeholder={t('enterPaymentTerm') || 'Enter payment term'}
              disabled={isLoading}
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={3}>
            <CFormLabel htmlFor="OrderDatetime">{t('orderDatetime')}</CFormLabel>
            <CFormInput
              type="date"
              id="OrderDatetime"
              name="OrderDatetime"
              value={formatDateForInput(formData.OrderDatetime)}
              onChange={handleInputChange}
              invalid={!!errors.OrderDatetime}
              disabled={isLoading}
            />
            {errors.OrderDatetime && <div className="invalid-feedback d-block">{errors.OrderDatetime}</div>}
          </CCol>
          <CCol md={3}>
            <CFormLabel htmlFor="ProductionStartDatetime">{t('productionStartDatetime')}</CFormLabel>
            <CFormInput
              type="date"
              id="ProductionStartDatetime"
              name="ProductionStartDatetime"
              value={formatDateForInput(formData.ProductionStartDatetime)}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </CCol>
          <CCol md={3}>
            <CFormLabel htmlFor="ProductionEndDatetime">{t('productionEndDatetime')}</CFormLabel>
            <CFormInput
              type="date"
              id="ProductionEndDatetime"
              name="ProductionEndDatetime"
              value={formatDateForInput(formData.ProductionEndDatetime)}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </CCol>
          <CCol md={3}>
            <CFormLabel htmlFor="OrderDeadlineDatetime">{t('orderDeadlineDatetime')}</CFormLabel>
            <CFormInput
              type="date"
              id="OrderDeadlineDatetime"
              name="OrderDeadlineDatetime"
              value={formatDateForInput(formData.OrderDeadlineDatetime)}
              onChange={handleInputChange}
              invalid={!!errors.OrderDeadlineDatetime}
              disabled={isLoading}
            />
            {errors.OrderDeadlineDatetime && <div className="invalid-feedback d-block">{errors.OrderDeadlineDatetime}</div>}
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel htmlFor="costPriceWhole">
              {t('productCostPrice')}
              {getCurrencySymbolByGuid(formData.CostCurrencyGuid) &&
                <span className="currency-unit-info">({getCurrencySymbolByGuid(formData.CostCurrencyGuid)})</span>
              }
            </CFormLabel>
            <div className="price-input-group">
              <div
                className="currency-symbol"
                onClick={() => setShowCostCurrencyModal(true)}
                style={{ cursor: 'pointer' }}
              >
                {getCurrencySymbolByGuid(formData.CostCurrencyGuid) || '₺'}
              </div>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="costPriceWhole"
                  className="price-whole"
                  value={priceInputs.costPriceWhole}
                  onChange={(e) => handlePriceInputChange('cost', 'whole', e.target.value)}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 'cost', 'whole')}
                  onFocus={() => handleDecimalFocus('cost', 'whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="costPriceDecimal"
                  className="price-decimal"
                  value={priceInputs.costPriceDecimal}
                  onChange={(e) => handlePriceInputChange('cost', 'decimal', e.target.value)}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 'cost', 'decimal')}
                  onFocus={() => handleDecimalFocus('cost', 'decimal')}
                  onBlur={() => handleDecimalBlur('cost', 'decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.ProductCostPrice &&
              <div className="invalid-feedback d-block">{errors.ProductCostPrice}</div>
            }
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="salesPriceWhole">
              {t('productSalesPrice')}
              {getCurrencySymbolByGuid(formData.SalesCurrencyGuid) &&
                <span className="currency-unit-info">({getCurrencySymbolByGuid(formData.SalesCurrencyGuid)})</span>
              }
            </CFormLabel>
            <div className={`price-input-group ${errors.ProductSalesPrice ? 'is-invalid' : ''}`}>
              <div
                className="currency-symbol"
                onClick={() => setShowSalesCurrencyModal(true)}
                style={{ cursor: 'pointer' }}
              >
                {getCurrencySymbolByGuid(formData.SalesCurrencyGuid) || '₺'}
              </div>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="salesPriceWhole"
                  className="price-whole"
                  value={priceInputs.salesPriceWhole}
                  onChange={(e) => handlePriceInputChange('sales', 'whole', e.target.value)}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 'sales', 'whole')}
                  onFocus={() => handleDecimalFocus('sales', 'whole')}
                  placeholder="0"
                  disabled={isLoading}
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="salesPriceDecimal"
                  className="price-decimal"
                  value={priceInputs.salesPriceDecimal}
                  onChange={(e) => handlePriceInputChange('sales', 'decimal', e.target.value)}
                  onKeyDown={(e) => handleDecimalKeyDown(e, 'sales', 'decimal')}
                  onFocus={() => handleDecimalFocus('sales', 'decimal')}
                  onBlur={() => handleDecimalBlur('sales', 'decimal')}
                  placeholder="00"
                  maxLength="2"
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.ProductSalesPrice &&
              <div className="invalid-feedback d-block">{errors.ProductSalesPrice}</div>
            }
          </CCol>
          <CCol md={4}>
            <CFormLabel htmlFor="totalPriceWhole">
              {t('productTotalPrice')}
              {getCurrencySymbolByGuid(formData.SalesCurrencyGuid) &&
                <span className="currency-unit-info">({getCurrencySymbolByGuid(formData.SalesCurrencyGuid)})</span>
              }
            </CFormLabel>
            <div className={`price-input-group readonly ${errors.ProductTotalPrice ? 'is-invalid' : ''}`}>
              <div className="currency-symbol">
                {getCurrencySymbolByGuid(formData.SalesCurrencyGuid) || '₺'}
              </div>
              <div className="price-input-section">
                <CFormInput
                  type="text"
                  id="totalPriceWhole"
                  className="price-whole"
                  value={priceInputs.totalPriceWhole}
                  readOnly
                  disabled
                  placeholder="0"
                />
                <span className="decimal-separator">,</span>
                <CFormInput
                  type="text"
                  id="totalPriceDecimal"
                  className="price-decimal"
                  value={priceInputs.totalPriceDecimal}
                  readOnly
                  disabled
                  placeholder="00"
                />
              </div>
            </div>
            {errors.ProductTotalPrice &&
              <div className="invalid-feedback d-block">{errors.ProductTotalPrice}</div>
            }
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="OrderDescription">{t('orderDescription')}</CFormLabel>
            <CFormInput
              type="text"
              id="OrderDescription"
              name="OrderDescription"
              value={formData.OrderDescription}
              onChange={handleInputChange}
              placeholder={t('orderDescriptionPlaceholder')}
              disabled={isLoading}
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="Note">{t('note')}</CFormLabel>
            <CFormInput
              type="text"
              id="Note"
              name="Note"
              value={formData.Note}
              onChange={handleInputChange}
              placeholder={t('notePlaceholder') || 'Not ekleyin...'}
              disabled={isLoading}
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="ShipmentPlace">{t('shipmentPlace') || 'Shipment Place'}</CFormLabel>
            <CFormInput
              type="text"
              id="ShipmentPlace"
              name="ShipmentPlace"
              value={formData.ShipmentPlace}
              onChange={handleInputChange}
              placeholder={t('enterShipmentPlace') || 'Enter shipment place'}
              disabled={isLoading}
            />
          </CCol>
        </CRow>
        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>

      {/* Currency selection modals */}
      <CModal
        visible={showCostCurrencyModal}
        onClose={() => setShowCostCurrencyModal(false)}
        className="currency-modal"
      >
        <CModalHeader className="currency-modal-header">
          <CModalTitle className="d-flex align-items-center">
            <span className="modal-currency-icon">💰</span>
            {t('selectCostCurrency') || 'Maliyet Para Birimi Seçin'}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-0">
          <div className="currency-search-container">
            <CFormInput
              type="text"
              placeholder={t('searchCurrency') || 'Para birimi ara...'}
              className="currency-search-input"
              value={costSearchTerm}
              onChange={(e) => {
                const term = e.target.value;
                setCostSearchTerm(term);
                const st = term.toLowerCase();
                const filtered = currencyOptions.filter(currency =>
                  (currency.CurrencyName || '').toLowerCase().includes(st) ||
                  (currency.CurrencyCode || '').toLowerCase().includes(st)
                );
                setFilteredCostCurrencies(filtered);
              }}
            />
          </div>
          <CListGroup flush>
            {(filteredCostCurrencies.length > 0 ? filteredCostCurrencies : []).map((item) => (
              <CListGroupItem
                key={item.PrdCurrencyGuid}
                action
                className="currency-list-item"
                onClick={() => {
                  setFormData(prev => ({ ...prev, CostCurrencyGuid: item.PrdCurrencyGuid }));
                  setShowCostCurrencyModal(false);
                  setCostSearchTerm('');
                  setFilteredCostCurrencies(currencyOptions);
                }}
              >
                <div className="currency-item-content">
                  <div className="currency-symbol-display">
                    {getCurrencySymbolByGuid(item.PrdCurrencyGuid) || '💱'}
                  </div>
                  <div className="currency-info">
                    <div className="currency-name">{item.CurrencyName}</div>
                    <div className="currency-code">{item.CurrencyCode}</div>
                  </div>
                  {formData.CostCurrencyGuid === item.PrdCurrencyGuid && (
                    <div className="currency-selected-indicator">
                      <span className="selected-check">✓</span>
                    </div>
                  )}
                </div>
              </CListGroupItem>
            ))}
          </CListGroup>
        </CModalBody>
      </CModal>

      <CModal
        visible={showSalesCurrencyModal}
        onClose={() => setShowSalesCurrencyModal(false)}
        className="currency-modal"
      >
        <CModalHeader className="currency-modal-header">
          <CModalTitle className="d-flex align-items-center">
            <span className="modal-currency-icon">💰</span>
            {t('selectSalesCurrency') || 'Satış Para Birimi Seçin'}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-0">
          <div className="currency-search-container">
            <CFormInput
              type="text"
              placeholder={t('searchCurrency') || 'Para birimi ara...'}
              className="currency-search-input"
              value={salesSearchTerm}
              onChange={(e) => {
                const term = e.target.value;
                setSalesSearchTerm(term);
                const st = term.toLowerCase();
                const filtered = currencyOptions.filter(currency =>
                  (currency.CurrencyName || '').toLowerCase().includes(st) ||
                  (currency.CurrencyCode || '').toLowerCase().includes(st)
                );
                setFilteredSalesCurrencies(filtered);
              }}
            />
          </div>
          <CListGroup flush>
            {(filteredSalesCurrencies.length > 0 ? filteredSalesCurrencies : []).map((item) => (
              <CListGroupItem
                key={item.PrdCurrencyGuid}
                action
                className="currency-list-item"
                onClick={() => {
                  setFormData(prev => ({ ...prev, SalesCurrencyGuid: item.PrdCurrencyGuid }));
                  setShowSalesCurrencyModal(false);
                  setSalesSearchTerm('');
                  setFilteredSalesCurrencies(currencyOptions);
                }}
              >
                <div className="currency-item-content">
                  <div className="currency-symbol-display">
                    {getCurrencySymbolByGuid(item.PrdCurrencyGuid) || '💱'}
                  </div>
                  <div className="currency-info">
                    <div className="currency-name">{item.CurrencyName}</div>
                    <div className="currency-code">{item.CurrencyCode}</div>
                  </div>
                  {formData.SalesCurrencyGuid === item.PrdCurrencyGuid && (
                    <div className="currency-selected-indicator">
                      <span className="selected-check">✓</span>
                    </div>
                  )}
                </div>
              </CListGroupItem>
            ))}
          </CListGroup>
        </CModalBody>
      </CModal>
    </div>
  );
};

OrderInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default OrderInputs;
